import { useCallback, useEffect, useState } from "react";
import ProjectCard from "../ProjectCard";
import { useDispatch, useSelector } from "react-redux";
import { setActiveSection } from "../../redux/actions/dashboardActions";
import ModalInfo from "../ModalInfo";
import { loadUserProfile } from "../../redux/actions/authActions";
import AppelOffreCard from "../AppelOffreCard";
import { sendLikeDonneurOrdre } from "../../redux/actions/likeActions";

export default function SearchAppelsOffres() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [secteurs, setSecteurs] = useState([]);
    const [selectedSecteur, setSelectedSecteur] = useState(null);
    const [error, setError] = useState(null);
    const { token, user } = useSelector((state) => state.auth);
    const { activeSubscription } = useSelector(state => state.subscriptionOffer);
    const [showModalInfo, setShowModalInfo] = useState(false);
    const nbCreditsLikesStandard = user.profilPrestatataire?.creditLikesStandard;
    const [search, setSearch] = useState("");
    const [projets, setProjets] = useState([]);
    const [total, setTotal] = useState(0);

    const [pagination, setPagination] = useState({
        limit: 5,
        offset: 0,
    });

    /**
     * Chargement des secteurs
     */
    const refreshSecteurs = useCallback(() => {
        setLoading(true);

        fetch(`${apiUrl}/api/secteurs`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(
                        "Erreur lors du chargement des secteurs"
                    );
                }

                return res.json();
            })
            .then((data) => {
                setSecteurs(data);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [apiUrl]);

    useEffect(() => {
        refreshSecteurs();
    }, [refreshSecteurs]);

    /**
     * Recherche des projets
     */
    const refreshAppelsOffres = useCallback(() => {
        setLoading(true);

        const params = new URLSearchParams();

        params.append("limit", pagination.limit);
        params.append("offset", pagination.offset);
        params.append("likerId",user.idUser);

        // Recherche texte
        if (search.trim() !== "") {
            params.append("search", search.trim());
        }

        // Secteur
        if (selectedSecteur) {
            params.append("secteurId", selectedSecteur.idSecteur);
        }

        fetch(`${apiUrl}/api/search/appelsOffres?${params.toString()}`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(
                        "Erreur lors de la recherche des appels d'offres"
                    );
                }

                return res.json();
            })
            .then((data) => {
                setProjets(data.results || []);
                setTotal(data.total || 0);
            })
            .catch((err) => {
                console.error(err);
                setProjets([]);
                setTotal(0);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [
        apiUrl,
        search,
        selectedSecteur,
        pagination.limit,
        pagination.offset,
    ]);

    /**
     * Recherche au premier chargement
     * et à chaque modification des critères/pagination
     */
    useEffect(() => {
        refreshAppelsOffres();
    }, [refreshAppelsOffres]);

    /**
     * Nombre total de pages
     */
    const totalPages = Math.ceil(total / pagination.limit);

    /**
     * Page courante
     */
    const currentPage =
        Math.floor(pagination.offset / pagination.limit) + 1;

    /**
     * Changement de page
     */
    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) {
            return;
        }

        setPagination((prev) => ({
            ...prev,
            offset: (page - 1) * prev.limit,
        }));
    };

    /**
     * Page précédente
     */
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    /**
     * Page suivante
     */
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    /**
     * Modification du secteur
     */
    const handleChangeSecteur = (e) => {
        const value = e.target.value;

        // Tout secteur
        if (value === "") {
            setSelectedSecteur(null);

            // Retour à la première page
            setPagination((prev) => ({
                ...prev,
                offset: 0,
            }));

            return;
        }

        const secteur = secteurs.find(
            (s) => s.idSecteur === parseInt(value, 10)
        );

        setSelectedSecteur(secteur || null);

        // Retour à la première page
        setPagination((prev) => ({
            ...prev,
            offset: 0,
        }));
    };

    const handleLike = async (currentProject) => {
            if (!currentProject) return;
    
            try {
                await dispatch(sendLikeDonneurOrdre(currentProject.idProjet, null, token));
                dispatch(loadUserProfile(token));
                refreshAppelsOffres();
            } catch (error) {
                console.error("Erreur lors du like :", error.message);
    
                // 💡 Détection du message spécifique
                if (error.message.includes("Crédits likes insuffisants")) {
                    setShowModalInfo(true);
                } else {
                    setError(error.message || "Une erreur est survenue");
                }
            }
        };
    /**
     * Modification de la recherche texte
     */
    const handleSearchChange = (e) => {
        setSearch(e.target.value);

        // Retour à la première page
        setPagination((prev) => ({
            ...prev,
            offset: 0,
        }));
    };
    if (showModalInfo) {
        return (
            <ModalInfo
                message="Vous devez acheter un crédit like pour pouvoir liker un appel d'offre"
                txtBtnCancel={"OK"}
                txtBtnValid={"VOIR LES OFFRES"}
                onValid={() => {
                    dispatch(setActiveSection("offres"));
                    setShowModalInfo(false);
                }}
                onCancel={() => setShowModalInfo(false)}
            />
        );
    }
    return (
        <div>
            {/* Recherche texte */}
            <div className="inscriptionPrestataireModal-input-form">
                <label>Rechercher un appel d'offre</label>

                <input
                    type="text"
                    value={search}
                    onChange={handleSearchChange}
                    placeholder="Rechercher un appel d'offre..."
                />
            </div>

            {/* Secteur */}
            <div className="inscriptionPrestataireModal-input-form">
                <label>Secteur d'activité</label>

                <select
                    name="secteurCompany"
                    value={
                        selectedSecteur
                            ? String(selectedSecteur.idSecteur)
                            : ""
                    }
                    onChange={handleChangeSecteur}
                >
                    <option value="">Tout secteur</option>

                    {secteurs.map((secteur) => (
                        <option
                            key={secteur.idSecteur}
                            value={secteur.idSecteur}
                        >
                            {secteur.labelSecteur}
                        </option>
                    ))}
                </select>
            </div>

            {/* Résultats */}
            <div>
                {loading ? (
                    <p>Chargement...</p>
                ) : (
                    <>
                        <p>
                            {total} appel{total > 1 ? "s" : ""} d'offres trouvés
                            {total > 1 ? "s" : ""}
                        </p>

                        {projets.length === 0 ? (
                            <p>Aucun appel d'offres trouvé.</p>
                        ) : (
                            projets.map((projet) => (
                                <AppelOffreCard
                                    key={projet.idProjetDonneurOrdre}
                                    project={projet}
                                    onLike={()=>handleLike(projet)}
                                />
                            ))
                        )}
                    </>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        type="button"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1 || loading}
                    >
                        Précédent
                    </button>

                    {Array.from(
                        { length: totalPages },
                        (_, index) => index + 1
                    ).map((page) => (
                        <button
                            key={page}
                            type="button"
                            onClick={() => handlePageChange(page)}
                            disabled={loading}
                            className={
                                currentPage === page
                                    ? "active"
                                    : ""
                            }
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        type="button"
                        onClick={handleNextPage}
                        disabled={
                            currentPage === totalPages || loading
                        }
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
}